import { camelCase, groupBy } from 'lodash-es';
import Routes from '../routes.mjs';
import getOriginByLocale from './getOriginByLocale';
import isFrenchMultilingualSite from './isFrenchMultilingualSite';
import formatTimestampAsParameter from './formatTimestampAsParameter';
import getCategoryParameters from './getCategoryParameters';
import getLanguageWithLocale from './getLanguageWithLocale';
import closestLocales from './closestLocales.json';
import { NewsArticleTranslationsQuery } from './graphql/api/graphql';

export interface NewsArticleUrlFields {
  id: number;
  slug: string;
  insertedAt: string;
  autoTranslated: boolean;
  category?: {
    id: number;
    path?: string | null;
  } | null;
}

export type Locale = keyof typeof closestLocales;
type LocaleKey = keyof NewsArticleTranslationsQuery;

type LanguageWithMultipleLocales = 'de' | 'en' | 'es' | 'fr' | 'nl';
type Language = 'da' | 'fi' | 'it' | 'ja' | 'nb' | 'pl' | 'pt' | 'ru' | 'sv' | 'tr' | 'zh';

const getLocalesFromData = (
  data: NewsArticleTranslationsQuery,
): Locale[] => Object.keys(data).map((localeKey) => {
  const split = localeKey.search(/[A-Z]/);
  const language = localeKey.slice(0, split);
  const country = localeKey.slice(split).toUpperCase();
  return `${language}-${country}` as Locale;
}).toSorted();

export const getTranslationsByLanguages = (
  data: NewsArticleTranslationsQuery,
  currentLocale: Locale,
): Record<string, { locale: Locale; newsArticle: NewsArticleUrlFields }> => {
  const localesByLanguages = groupBy(
    getLocalesFromData(data),
    getLanguageWithLocale,
  ) as Record<Language, Locale[]>;

  return Object.fromEntries(
    Object.entries(localesByLanguages).flatMap(([language, locales]) => {
      if (getLanguageWithLocale(currentLocale) === language) {
        return [];
      }

      const closestLocalesRecord = closestLocales as Record<
        Locale,
        Record<LanguageWithMultipleLocales, Locale[]>
      >;
      const preferredLocales = language in (closestLocalesRecord[currentLocale])
        ? closestLocalesRecord[currentLocale][
          language as LanguageWithMultipleLocales
        ]
        : undefined;
      const chosenLocale = preferredLocales
        ? preferredLocales.find(
          (locale: Locale) => data[camelCase(locale) as LocaleKey]?.[0],
        )
        : locales[0];

      const newsArticle = chosenLocale
        ? data[camelCase(chosenLocale) as LocaleKey]?.[0] as (
          NewsArticleUrlFields | undefined
        )
        : undefined;

      if (!chosenLocale || !newsArticle?.autoTranslated) {
        return [];
      }

      return [[
        language,
        {
          locale: chosenLocale,
          newsArticle,
        },
      ]];
    }),
  );
};

export const getOriginalArticle = (data: NewsArticleTranslationsQuery) => {
  const locales = getLocalesFromData(data);

  const originalLocale = locales.find((locale) => {
    const article = data[
      camelCase(locale) as LocaleKey
    ]?.[0] as NewsArticleUrlFields | undefined;

    return article && !article.autoTranslated;
  });

  return originalLocale && {
    locale: originalLocale,
    newsArticle: data[
      camelCase(originalLocale) as LocaleKey
    ]?.[0] as NewsArticleUrlFields | undefined,
  };
};

export const getArticleUrl = (
  {
    id, slug, insertedAt, category,
  }: NewsArticleUrlFields,
  origin: string,
  locale: Locale,
) => `${getOriginByLocale(
  origin,
  locale,
)
}${Routes.findAndGetUrls('article', {
  ...isFrenchMultilingualSite(locale) ? {
    prefix: 'fr',
  } : {},
  id: String(id),
  slug,
  insertedAt: formatTimestampAsParameter(
    insertedAt,
  ),
  ...getCategoryParameters(category),
}).urls.as
}`;
